/* Types + presentation for `enable_radius_scoring`, shape verified against live flag-on runs of
   POST /api/concept-test-vitamin. Names come from the payload's tag, colour from the ladder
   position — ADR-0009. */

export type RadiusLevel = 0 | 1 | 2 | 3;

export interface RadiusSentence {
  text: string;
  radius: RadiusLevel;
  /** A quoted consumer verbatim on the top rung, a description in between, and null at the last. */
  backing?: string | null;
  /** v1 set `novel_insight` here, off its own scale — which is why `scale` is read first. */
  tag?: string | null;
}

export interface RadiusItem {
  radius: RadiusLevel;
  tag?: string | null;
  sentences: RadiusSentence[];
}

export interface RadiusThresholds {
  sim_grounded?: number;
  sim_inferred?: number;
  min_aggregate?: number;
  numeric_tolerance_pp?: number;
}

export interface RadiusBlock {
  method?: string;
  /** Absent on v1 payloads. Present and 2 since 2026-08-26. */
  scale_version?: number;
  /** The API's own rung names, keyed by level — rendered, never hardcoded. */
  scale?: Record<string, string>;
  /** v2's canonical rung ORDER. Absent in v1, where the scale's own keys stand in. */
  tags?: string[];
  thresholds?: RadiusThresholds;
  embeddings_available?: boolean;
  corpus_size?: number;
  corpus_consumers?: number;
  sentences_scored?: number;
  llm_adjudicated?: number;
  /** v1 keys the counts by level. */
  distribution?: Record<string, number>;
  /** v2's tag-keyed form of the same counts; read first — the numeric key is compatibility. */
  distribution_by_tag?: Record<string, number>;
  novel_count?: number;
  /** v2's name for `novel_count`. */
  directional_count?: number;
  failed_numeric_claims?: unknown[];
  /** Keyed by content-addressed path: "verdict", "insight.<headline>", "frictions.<name>", … */
  items?: Record<string, RadiusItem>;
}

/** How a rung reads, keyed by the API's tag rather than its level — ADR-0009. */
interface RadiusTagCopy {
  label: string;
  method: (thresholds?: RadiusThresholds) => string;
}

const DEFAULT_SIM_GROUNDED = 0.72;
const DEFAULT_MIN_AGGREGATE = 3;

const fromOwnWords = (t?: RadiusThresholds) =>
  `Similarity to a twin's own words ≥ ${t?.sim_grounded ?? DEFAULT_SIM_GROUNDED}`;
const acrossTwins = (t?: RadiusThresholds) =>
  `Aggregated across ${t?.min_aggregate ?? DEFAULT_MIN_AGGREGATE} or more twins`;
const fromFigures = () => 'Built from study figures rather than speech';
const fromNothing = () => 'Written by the analyst — nothing in the corpus supports it';

const RADIUS_TAG_COPY: Record<string, RadiusTagCopy> = {
  // scale_version 1
  grounded: { label: 'Grounded in a consumer verbatim', method: fromOwnWords },
  logically_derived: { label: 'Derived from study figures', method: fromFigures },
  novel: { label: 'No source in the corpus', method: fromNothing },
  // scale_version 2
  observed: { label: 'Observed in a consumer verbatim', method: fromOwnWords },
  measured: { label: 'Measured from study figures', method: fromFigures },
  directional: { label: 'No source in the corpus', method: fromNothing },
  // both — v1 rung 1, v2 rung 2, the same meaning either way
  inferred: { label: 'Inferred from a shared pattern', method: acrossTwins },
};

/** Colour by ladder position, not by tag: rung 0 is always the best-supported. */
const RADIUS_COLORS: readonly { color: string; colorOnDark: string }[] = [
  { color: '#15803d', colorOnDark: '#4ade80' },
  { color: '#1d4ed8', colorOnDark: '#93c5fd' },
  { color: '#b45309', colorOnDark: '#fcd34d' },
  { color: '#dc2626', colorOnDark: '#fca5a5' },
];

export const RADIUS_LEVEL_COUNT = RADIUS_COLORS.length;

export interface RadiusRung {
  level: RadiusLevel;
  /** The API's own name for the rung, for reference against the raw payload. */
  tag: string;
  label: string;
  color: string;
  /** Lighter variant, for marks sitting on the dark hero panel. */
  colorOnDark: string;
}

const colorsAt = (level: number) => RADIUS_COLORS[level] ?? RADIUS_COLORS[RADIUS_LEVEL_COUNT - 1];

const rungAt = (level: number, tag: string): RadiusRung => ({
  level: level as RadiusLevel,
  tag,
  label: RADIUS_TAG_COPY[tag]?.label ?? tag,
  ...colorsAt(level),
});

/** The rungs in the payload's own order — `tags` in v2, the scale's keys in v1, levels if neither. */
export const radiusRungs = (block?: RadiusBlock): RadiusRung[] => {
  const scale = block?.scale;
  const ordered = block?.tags?.length
    ? block.tags
    : scale
      ? Object.keys(scale)
          .sort((a, b) => Number(a) - Number(b))
          .map((key) => scale[key])
      : [];
  const names = ordered.length
    ? ordered
    : Array.from({ length: RADIUS_LEVEL_COUNT }, (_, i) => String(i));
  return names.map((tag, level) => rungAt(level, tag));
};

/** The tag a sentence carries. The scale wins over `sentence.tag` — see that field's note. */
export const radiusTagFor = (sentence: RadiusSentence, block?: RadiusBlock): string =>
  block?.scale?.[String(sentence.radius)] ?? sentence.tag ?? String(sentence.radius);

export const radiusRungFor = (sentence: RadiusSentence, block?: RadiusBlock): RadiusRung =>
  rungAt(sentence.radius, radiusTagFor(sentence, block));

/** How the rung was decided — null for an unknown tag, never a neighbour's explanation. */
export const radiusMethodFor = (tag: string, thresholds?: RadiusThresholds): string | null =>
  RADIUS_TAG_COPY[tag]?.method(thresholds) ?? null;
